import React from 'react'
import { 
  Clock, Euro, Shield, Wrench, Calendar, 
  CheckCircle, XCircle, User, Edit
} from 'lucide-react'

import Button from '../ui/Button'
import Badge from '../ui/Badge'
import { Servico } from '../../types'
import { formatarPreco, getCategoriaLabel, getStatusServico } from '../../services/servicos'

interface ServicoDetailsProps {
  servico: Servico
  onEdit?: () => void
  onClose?: () => void
}

const ServicoDetails: React.FC<ServicoDetailsProps> = ({
  servico,
  onEdit,
  onClose
}) => {
  const statusServico = getStatusServico(servico.ativo)

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wrench className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {servico.nome}
              </h3>
              <div className="flex items-center space-x-3 mt-1">
                <Badge 
                  color={statusServico.color}
                  variant="dot"
                >
                  {statusServico.label}
                </Badge>
                <span className="text-sm text-gray-500">
                  ID: {servico.id}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2">
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
              >
                <Edit className="w-4 h-4 mr-1" />
                Editar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4 space-y-6">
        
        {/* Informações Básicas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Categoria e Descrição */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <div className="flex items-center space-x-2">
                <Badge variant="outline">
                  {getCategoriaLabel(servico.categoria)}
                </Badge>
              </div>
            </div>

            {servico.descricao && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {servico.descricao}
                </p>
              </div>
            )}
          </div>

          {/* Informações Financeiras e Tempos */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Euro className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Preço</span>
                </div>
                <span className="text-xl font-bold text-blue-900">
                  {formatarPreco(servico.preco)}
                </span>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">Tempo Estimado</span>
                </div>
                <span className="text-sm font-semibold text-green-900">
                  {servico.tempoEstimado}
                </span>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Garantia</span>
                </div>
                <span className="text-sm font-semibold text-purple-900">
                  {servico.garantia}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Equipamentos Suportados */}
        {servico.equipamentosSuportados && servico.equipamentosSuportados.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Equipamentos Suportados
            </label>
            <div className="flex flex-wrap gap-2">
              {servico.equipamentosSuportados.map((equipamento, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="bg-gray-50"
                >
                  {equipamento}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Observações */}
        {servico.observacoes && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações
            </label>
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-200">
              <p className="text-sm text-gray-900">
                {servico.observacoes}
              </p>
            </div>
          </div>
        )}

        {/* Informações do Sistema */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Informações do Sistema
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Criado em:</span>
              <span className="text-gray-900">
                {new Date(servico.createdAt).toLocaleDateString('pt-PT', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Atualizado em:</span>
              <span className="text-gray-900">
                {new Date(servico.updatedAt).toLocaleDateString('pt-PT', {
                  year: 'numeric',
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              {servico.ativo ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-gray-500">Status:</span>
              <span className={`font-medium ${servico.ativo ? 'text-green-600' : 'text-red-600'}`}>
                {servico.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      {onClose && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fechar
          </Button>
        </div>
      )}
    </div>
  )
}

export default ServicoDetails